import { useQuery } from "@tanstack/react-query";
import { CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import {
  funnelKey,
  funnelsKey,
  getFunnel,
  getFunnels,
  getPages,
  messageFor,
  pagesKey,
  type FunnelDetail,
  type VariantStats,
} from "./api";
import { FunnelControls, selectedFunnel, useControls, type Controls } from "./controls";
import {
  chartRows,
  comparisonLine,
  formatCount,
  formatDay,
  formatFigure,
  formatPct,
  formatSignups,
  languageChips,
  languageName,
  variantLabel,
  variantsOf,
  verdictLine,
  windowStart,
} from "./format";
import { OrdersStamp, OrdersUnavailable } from "./OrderFigures";
import { getFunnelPages, getFunnelSources, statsPagesKey, statsSourcesKey, type VariantOrders } from "./stats-api";
import {
  formatMoney,
  formatOrders,
  localeParam,
  noVersionLine,
  ordersFor,
  sourceFilterSpec,
  sourceLabel,
  sourceParam,
  sourceValue,
  WORKER_SOURCE,
} from "./stats-format";

/** Token colours per variant, in letter order. No raw hex (lint:tokens). */
const VARIANT_COLOURS = ["var(--color-ks-purple)", "var(--color-ks-purple-3)", "var(--color-ks-grey-1)"];

/** URL keys shared with the Pages tab, so a chosen source or language carries between them. */
const SOURCE = "src";
const LANG = "lang";

/** The funnel stats read for the cards: `rows` is undefined while it loads or when it failed. */
type OrdersState = { rows: VariantOrders[] | undefined; dataAsOf: string | null | undefined; error: unknown };

/**
 * One funnel's split test: a card per version, a plain-language verdict line,
 * and the daily chart. Visitors and sign-ups are the Worker's, orders and
 * revenue are the funnel stats API's; nothing here decides a winner.
 */
export function SplitTestTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const funnels = useQuery({
    queryKey: funnelsKey(controls.days),
    queryFn: () => getFunnels(controls.days),
  });

  const list = funnels.data?.data.funnels ?? [];
  // No All here (FN-1): a split test is one funnel's. Default = the busiest funnel in the window.
  const selected = selectedFunnel(controls.funnel, list, false);
  // One cut for the whole tab: cards, verdict, chart and orders all read it.
  const cut = { source: sourceParam(controls.values[SOURCE]), locale: localeParam(controls.values[LANG]) };

  const detail = useQuery({
    queryKey: funnelKey(selected ?? "", controls.days, cut),
    queryFn: () => getFunnel(selected as string, controls.days, cut),
    enabled: selected !== null,
  });

  // A second, independent read: the cards never wait for it and never fail with it.
  const orders = useQuery({
    queryKey: statsPagesKey(selected ?? "", controls.days, cut),
    queryFn: () => getFunnelPages(selected as string, controls.days, cut),
    enabled: selected !== null,
  });

  if (funnels.isError) {
    return (
      <Alert>
        <AlertTitle>Landing page figures unavailable</AlertTitle>
        <AlertDescription>{messageFor(funnels.error, areaName)}</AlertDescription>
      </Alert>
    );
  }
  if (funnels.isPending) return <Skeleton className="h-96 w-full" />;
  if (selected === null) {
    return <p className="text-sm text-muted-foreground">No landing page has recorded a visit or a sign-up yet.</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <FunnelControls controls={controls} funnels={list} selected={selected} />
      <CutFilters controls={controls} funnel={selected} source={cut.source} locale={cut.locale} />
      {detail.isError ? (
        <Alert>
          <AlertTitle>Split test unavailable</AlertTitle>
          <AlertDescription>{messageFor(detail.error, areaName)}</AlertDescription>
        </Alert>
      ) : detail.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <SplitTestBody
          detail={detail.data.data}
          orders={{
            rows: orders.data?.data.rows,
            dataAsOf: orders.data?.meta.data_as_of,
            error: orders.isError ? orders.error : null,
          }}
          areaName={areaName}
        />
      )}
    </div>
  );
}

/**
 * The source and language chips. Their choices come from unfiltered reads (the
 * funnel's sources and live pages), so choosing one never hides the others.
 */
function CutFilters({
  controls,
  funnel,
  source,
  locale,
}: {
  controls: Controls;
  funnel: string;
  source: string | null;
  locale: string | null;
}) {
  const sources = useQuery({
    queryKey: statsSourcesKey(funnel, controls.days),
    queryFn: () => getFunnelSources(funnel, controls.days),
  });
  const pages = useQuery({
    queryKey: pagesKey(controls.days),
    queryFn: () => getPages(controls.days),
  });
  const funnelPages = pages.data?.data.funnels.find((f) => f.funnel_slug === funnel)?.pages ?? [];
  const chips = languageChips(funnelPages);
  const langSpec: FilterSpec = {
    name: LANG,
    label: "Language · sign-ups",
    kind: "choice",
    options: [
      { value: "", label: "All" },
      ...chips.map((chip) => ({ value: chip.locale, label: `${languageName(chip.locale)} ${formatSignups(chip.signups)}` })),
      ...(locale && !chips.some((chip) => chip.locale === locale) ? [{ value: locale, label: languageName(locale) }] : []),
    ],
  };
  // Only sources the Worker can filter on; the cards would fail on any other.
  const sourceRows = (sources.data?.data.rows ?? []).filter((row) => WORKER_SOURCE.test(sourceValue(row.source)));
  const showSource = sourceRows.length > 0 || source !== null;
  const showLang = chips.length > 1 || locale !== null;
  const scope = [locale && languageName(locale), source && sourceLabel(source)].filter(Boolean).join(" · ");
  return (
    <>
      {(showSource || showLang) && (
        <div className="flex flex-col gap-2 overflow-x-auto">
          {showSource && (
            <FilterBar
              layout="segmented"
              filters={[sourceFilterSpec(SOURCE, sourceRows, source)]}
              values={controls.values}
              onChange={controls.onChange}
            />
          )}
          {showLang && (
            <FilterBar layout="segmented" filters={[langSpec]} values={controls.values} onChange={controls.onChange} />
          )}
        </div>
      )}
      {scope && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Showing {scope} only: every card, the verdict, the chart and the orders below.
        </p>
      )}
    </>
  );
}

export function SplitTestBody({
  detail,
  orders,
  areaName,
}: {
  detail: FunnelDetail;
  orders: OrdersState;
  areaName: string;
}) {
  const variants = [...detail.variants].sort((a, b) => a.variant.localeCompare(b.variant));
  // Taps show only on a funnel whose page has an app button that has been tapped (app-entry).
  const showTaps = variants.some((variant) => (variant.clicks ?? 0) > 0);
  const noVersion = noVersionLine(orders.rows);
  const leftOut = comparisonLine(detail.comparison);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {variants.map((variant) => (
          <VariantCard
            key={variant.variant}
            variant={variant}
            orders={ordersFor(orders.rows, variant.variant)}
            showTaps={showTaps}
          />
        ))}
      </div>
      {leftOut && <p className="text-sm text-muted-foreground">{leftOut}</p>}
      {orders.error ? (
        <OrdersUnavailable error={orders.error} areaName={areaName} />
      ) : (
        <OrdersStamp dataAsOf={orders.dataAsOf} />
      )}
      {noVersion && <p className="text-sm text-muted-foreground">{noVersion}</p>}
      <p className="text-base font-medium text-ks-ink">{verdictLine(detail)}</p>
      {detail.signups_before_counting > 0 && (
        <p className="text-sm text-muted-foreground">
          {formatCount(detail.signups_before_counting)} sign-ups arrived before visitor counting started and are not in
          the rates.
        </p>
      )}
      <DailyChart detail={detail} />
    </>
  );
}

function VariantCard({
  variant,
  orders,
  showTaps,
}: {
  variant: VariantStats;
  orders: VariantOrders | undefined;
  showTaps: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <h3 className="text-base font-semibold text-ks-ink">{variantLabel(variant.variant)}</h3>
        <dl className="grid grid-cols-3 gap-2 text-sm">
          <Figure label="Visitors" value={formatCount(variant.new_visitors)} />
          <Figure label="Sign-ups" value={formatCount(variant.signups)} />
          <Figure label="Conversion" value={formatPct(variant.conversion_pct)} />
          {/* A version the service has no row for shows a dash: unknown, not zero. */}
          <Figure label="Orders" value={formatOrders(orders?.orders)} />
          <Figure label="Revenue" value={formatMoney(orders?.revenue_gbp)} />
          <Figure label="Revenue per visitor" value={formatMoney(orders?.revenue_per_visitor)} />
          {showTaps && <Figure label="Taps" value={formatFigure(variant.clicks)} />}
        </dl>
      </CardContent>
    </Card>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-ks-ink">{value}</dd>
    </div>
  );
}

function DailyChart({ detail }: { detail: FunnelDetail }) {
  const variants = variantsOf(detail);
  const start = windowStart(detail.days);
  const from = detail.counting_since === null ? null : detail.counting_since > start ? detail.counting_since : start;
  const data = chartRows(detail.daily, { from, to: new Date().toISOString().slice(0, 10), variants });
  return (
    <ChartFrame
      title="New visitors per day, by version"
      subtitle="Lines are new visitors; large dots are sign-ups. Days before counting started are a gap, not a zero."
      titleVisible
      pointCount={data.length}
      emptyMessage="No daily visits or sign-ups in this window yet."
    >
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis
          dataKey="day"
          tick={{ fontSize: 12 }}
          stroke="var(--color-ks-grey-1)"
          tickFormatter={(day: string) => formatDay(day)}
        />
        <YAxis tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" allowDecimals={false} width={48} />
        <Tooltip
          labelFormatter={(day) => formatDay(String(day))}
          contentStyle={{
            background: "var(--color-ks-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-ks-sm)",
            fontSize: 12,
          }}
        />
        {variants.map((variant, index) => {
          const colour = VARIANT_COLOURS[index % VARIANT_COLOURS.length];
          return [
            <Line
              key={`visitors_${variant}`}
              type="linear"
              dataKey={`visitors_${variant}`}
              name={`${variantLabel(variant)} visitors`}
              stroke={colour}
              strokeWidth={2}
              dot={{ r: 2.5, fill: colour, stroke: colour }}
              connectNulls={false}
              isAnimationActive={false}
            />,
            <Line
              key={`signups_${variant}`}
              type="linear"
              dataKey={`signups_${variant}`}
              name={`${variantLabel(variant)} sign-ups`}
              stroke="none"
              dot={{ r: 4, fill: colour, stroke: colour }}
              activeDot={{ r: 5 }}
              connectNulls={false}
              isAnimationActive={false}
            />,
          ];
        })}
      </ComposedChart>
    </ChartFrame>
  );
}
